import { config } from './config.js';

// ROW UNITS — the coordinate a pinned node's vertical position persists in.
//
// A pin stores `gy` in GRID units so a spacing change carries the pinned node
// WITH the reflowed family (D2, TGCLOUD-1458). That was `y / LEVEL_HEIGHT`,
// which only inverts while every generation is the same height. Per-generation
// vertical spacing (TGCLOUD-1610) breaks that: there is no single divisor.
//
// So `gy` keeps its stored meaning and gains a precise one — ROW UNITS. Under
// the historical uniform layout row i sits at (i + 1) * LEVEL_HEIGHT, so
// `gy = y / LEVEL_HEIGHT` already read as "row index + 1, plus a fraction of
// the gap below it". These two functions are that reading made piecewise, so:
//
//   • an integer gy lands EXACTLY on its row, whatever the gaps around it —
//     which is the property pinUnits.test.js pins ("the pin travels with the
//     family");
//   • on a uniform layout every branch reduces to gy * LEVEL_HEIGHT, so pins
//     saved before this change resolve to the same pixel they always did;
//   • `rows == null` (uniform spacing, or the flag off) short-circuits to the
//     legacy expressions, byte-for-byte.
//
// `rows` is { ys, gapBelowLast }: `ys[i]` is row i's laid-out y (ascending),
// `gapBelowLast` the pitch to extrapolate below the last row. Gaps are always
// > 0, so the map is a strictly increasing bijection and the two functions are
// exact inverses of one another.

// Row units → pixels.
export function yFromRowUnits(u, rows) {
  if (!Number.isFinite(u)) return undefined;
  if (!rows || !rows.ys || !rows.ys.length) return u * config.LEVEL_HEIGHT;
  const ys = rows.ys;
  const R = ys.length;
  // Above the first row: interpolate from the canvas top, where u = 1 is row 0.
  if (u <= 1) return u * ys[0];
  // Below the last row: extrapolate on the last gap.
  if (u >= R) return ys[R - 1] + (u - R) * rows.gapBelowLast;
  const i = Math.floor(u) - 1; // 0 <= i <= R - 2
  return ys[i] + (u - (i + 1)) * (ys[i + 1] - ys[i]);
}

// Pixels → row units. `levelHeight` is only consulted on the legacy path, so a
// caller can pass the layout's own value rather than the live singleton.
export function rowUnitsFromY(y, rows, levelHeight) {
  if (!Number.isFinite(y)) return undefined;
  if (!rows || !rows.ys || !rows.ys.length) {
    const lh = Number(levelHeight) || config.LEVEL_HEIGHT;
    return y / lh;
  }
  const ys = rows.ys;
  const R = ys.length;
  if (y <= ys[0]) return ys[0] > 0 ? y / ys[0] : 0;
  if (y >= ys[R - 1]) return R + (y - ys[R - 1]) / rows.gapBelowLast;
  let i = 0;
  while (i < R - 1 && ys[i + 1] <= y) i++;
  return i + 1 + (y - ys[i]) / (ys[i + 1] - ys[i]);
}
